import rough from 'roughjs';
import { log } from '../../../logger.js';
import type { Bounds, D3Selection, Point } from '../../../types.js';
import { handleUndefinedAttr } from '../../../utils.js';
import type { MindmapOptions, Node, ShapeRenderOptions } from '../../types.js';
import intersect from '../intersect/index.js';
import { styles2String, userNodeOverrides } from './handDrawnShapeStyles.js';
import { getNodeClasses, labelHelper, updateNodeBounds } from './util.js';

export async function circle<T extends SVGGraphicsElement>(
  parent: D3Selection<T>,
  node: Node,
  options?: MindmapOptions | ShapeRenderOptions
) {
  const { labelStyles, nodeStyles } = styles2String(node);
  node.labelStyle = labelStyles;
  // A min width would inflate the radius on both axes, leaving short labels in a large, empty circle.
  const { shapeSvg, bbox, halfPadding } = await labelHelper(parent, node, getNodeClasses(node), {
    ignoreMinWidth: true,
  });

  // Calculate radius based on look type
  const labelPadding = 16;
  const padding = options?.padding ?? halfPadding;
  // Half the label box's diagonal, not half its width: a circle sized from the width alone
  // clips every label taller than it is wide, which is what a narrow wrappingWidth produces.
  const labelRadius = Math.sqrt(bbox.width ** 2 + bbox.height ** 2) / 2;
  // The diagonal already contains the label, so neo needs only one padding step (as doubleCircle's inner ring).
  const radius = node.look === 'neo' ? labelRadius + labelPadding : labelRadius + padding;

  let circleElem;
  const { cssStyles } = node;

  if (node.look === 'handDrawn') {
    // @ts-expect-error -- Passing a D3.Selection seems to work for some reason
    const rc = rough.svg(shapeSvg);
    const options = userNodeOverrides(node, {});
    const roughNode = rc.circle(0, 0, radius * 2, options);

    circleElem = shapeSvg.insert(() => roughNode, ':first-child');
    circleElem.attr('class', 'basic label-container').attr('style', handleUndefinedAttr(cssStyles));
  } else {
    circleElem = shapeSvg
      .insert('circle', ':first-child')
      .attr('class', 'basic label-container')
      .attr('style', nodeStyles)
      .attr('r', radius)
      .attr('cx', 0)
      .attr('cy', 0);
  }

  updateNodeBounds(node, circleElem);
  node.calcIntersect = function (bounds: Bounds, point: Point) {
    const radius = bounds.width / 2;
    return intersect.circle(bounds, radius, point);
  };
  node.intersect = function (point) {
    log.info('Circle intersect', node, radius, point);
    return intersect.circle(node, radius, point);
  };

  return shapeSvg;
}
